import { useDispatch, useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';

import {
  Box,
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Text,
} from '@chakra-ui/react';

import { ReducerStatus } from 'utils/constants';
import ResultModal from 'components/common/modals/ResultModal';
import { removeSubQuest } from 'store/modules/subquest/subquest-action';
import { subquestActions } from 'store/modules/subquest/subquest-slice';
import LoadingSpinner from 'components/common/loaders/LoadingSpinner';

function DeleteSubQuestMd() {
  const dispatch = useDispatch();

  const removeSubQuestState = useSelector(
    (state) => state.subquest.removeSubQuest
  );

  const questId = useSelector((state) => state.subquest.questId);
  const relatedCourseQuests = useSelector(
    (state) => state.subquest.relatedCourseQuests
  );

  const renderRelatedCourseQuests = () => {
    if (!relatedCourseQuests?.courseQuests?.length) return null;

    return (
      <Text mt={4} fontSize="sm">
        <Text as="span" fontWeight="semibold">
          This sub-quest is also attached to:
        </Text>
        {relatedCourseQuests.courseQuests.map((item) => (
          <Text key={uuidv4()} ml={2} mt={2}>
            •{' '}
            <Text as="span" fontWeight="semibold">
              Course:
            </Text>{' '}
            {item.courseName} →{' '}
            <Text as="span" fontWeight="semibold">
              Quest:
            </Text>{' '}
            {item.questName} →{' '}
            <Text as="span" fontWeight="semibold">
              {item.subQuestName}
            </Text>
          </Text>
        ))}
      </Text>
    );
  };

  const handleToggleRemoveModal = (isOpen) => {
    dispatch(subquestActions.toggleRemoveModal(isOpen));
  };

  const handleRemoveSubQuest = () => {
    dispatch(
      removeSubQuest({
        questId,
        subQuestId: removeSubQuestState.subQuestId,
      })
    );
  };

  const handleResultButton = () => {
    dispatch(subquestActions.resetRemoveStatus());
    if (removeSubQuestState.status === ReducerStatus.SUCCEEDED) {
      handleToggleRemoveModal(false);
    }
  };

  return (
    <Box>
      {removeSubQuestState.status === ReducerStatus.SUCCEEDED ||
      removeSubQuestState.status === ReducerStatus.FAILED ? (
        <ResultModal
          isOpen={removeSubQuestState.modal}
          onSubmit={handleResultButton}
          isError={removeSubQuestState.status === ReducerStatus.FAILED}
          title={
            removeSubQuestState.status === ReducerStatus.SUCCEEDED
              ? 'Removed'
              : 'Server Error'
          }
          message={
            removeSubQuestState.status === ReducerStatus.SUCCEEDED
              ? 'The selected sub-quest has been deleted successfully.'
              : null
          }
        />
      ) : (
        <Modal
          isOpen={removeSubQuestState.modal}
          onClose={() => handleToggleRemoveModal(false)}
          isCentered
        >
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Confirmation Required</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              Are you sure you want to delete this sub-quest? Please note that
              all related sub-quest materials, including those used by other
              quests, will also be deleted.
              {relatedCourseQuests.status === ReducerStatus.LOADING ? (
                <Box mt={6}>
                  <LoadingSpinner message="Fetching if there are any courses with this sub-quest" />
                </Box>
              ) : (
                renderRelatedCourseQuests()
              )}
            </ModalBody>

            <ModalFooter>
              <Button
                colorScheme="gray"
                mr={3}
                onClick={() => handleToggleRemoveModal(false)}
              >
                Close
              </Button>
              <Button
                colorScheme="red"
                isLoading={removeSubQuestState.status === ReducerStatus.LOADING}
                onClick={() => handleRemoveSubQuest()}
                isDisabled={
                  relatedCourseQuests.status === ReducerStatus.LOADING
                }
              >
                Proceed
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      )}
    </Box>
  );
}

export default DeleteSubQuestMd;
